/* eslint-disable react/require-default-props */
import React from 'react';
import { useIntl } from 'react-intl';
import { Button, FlexColumn, FlexRow, Table, Typography } from 'design-system';
import PreviewChoiceIndicator from './PreviewChoiceIndicator';
import { getOptionId } from '../surveyPreviewUtils';

const idStr = (id) => (id != null ? String(id) : '');

function optionLabel(option) {
  return `${option.text}${option?.value ? ` (${option.value})` : ''}`;
}

export default function GridTypeAnswer({
  question,
  preview = false,
  value,
  onChange,
  interactive = false,
}) {
  const intl = useIntl();
  const rows = question?.gridRows ?? [];
  const options = question?.options ?? [];
  const isMulti = question?.gridInputType === 'multiselect';
  const gridValue = value && typeof value === 'object' ? value : {};

  if (!rows.length || !options.length) {
    return null;
  }

  const columns = [
    {
      key: 'label',
      label: intl.formatMessage({ id: 'aspect', defaultMessage: 'Aspect' }),
    },
    ...options.map((option, index) => {
      const optionId = idStr(getOptionId(option, index));
      return {
        key: optionId,
        label: optionLabel(option),
      };
    }),
  ];

  const data = rows.map((row) => ({
    _id: idStr(row._id) || String(row.order),
    label: row.text,
  }));

  const handleCellClick = (rowId, optionId) => {
    if (!interactive) return;

    if (isMulti) {
      const current = Array.isArray(gridValue[rowId]) ? gridValue[rowId] : [];
      const next = current.includes(optionId)
        ? current.filter((id) => id !== optionId)
        : [...current, optionId];
      onChange?.({ ...gridValue, [rowId]: next });
      return;
    }

    onChange?.({ ...gridValue, [rowId]: optionId });
  };

  const isCellSelected = (rowId, optionId) => {
    const cell = gridValue[rowId];
    if (isMulti) {
      return Array.isArray(cell) && cell.includes(optionId);
    }
    return cell === optionId;
  };

  const renderChoiceControl = (rowId, optionId, selected) => (
    <Button
      type="button"
      variant="link"
      className="cc-preview-grid__cell-btn"
      aria-pressed={selected}
      disabled={!interactive}
      onClick={() => handleCellClick(rowId, optionId)}
    >
      <PreviewChoiceIndicator type={isMulti ? 'checkbox' : 'radio'} selected={selected} />
    </Button>
  );

  const renderCell = ({ row, column }) => {
    if (column.key === 'label') {
      return (
        <Typography component="span" variant="body2" className="cc-preview-grid__row-label">
          {row.label}
        </Typography>
      );
    }

    const rowId = row._id;
    const optionId = column.key;
    const selected = isCellSelected(rowId, optionId);

    return (
      <FlexRow align="center" justify="center" fullWidth>
        {renderChoiceControl(rowId, optionId, selected)}
      </FlexRow>
    );
  };

  const rootClassName = [
    'cc-preview-grid',
    preview ? '' : 'grid-response-container mt-2',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClassName}>
      <div className="cc-preview-grid__desktop">
        <Table
          columns={columns}
          data={data}
          rowKey={(row) => row._id}
          renderCell={renderCell}
          layout="embedded"
          density="compact"
          tableLayout="auto"
          showVerticalDividers
          rowHover={false}
          showTitle={false}
          noDataMessage=""
        />
      </div>

      <FlexColumn align="stretch" fullWidth gap="var(--ds-space-3)" className="cc-preview-grid__mobile">
        {data.map((row) => {
          const rowId = row._id;
          return (
            <FlexColumn
              key={rowId}
              align="stretch"
              fullWidth
              className="cc-preview-grid__mobile-row"
            >
              <Typography
                component="p"
                variant="body2"
                fontWeight="semibold"
                className="cc-preview-grid__mobile-row-label"
              >
                {row.label}
              </Typography>
              <FlexColumn align="stretch" fullWidth className="cc-preview-grid__mobile-options">
                {options.map((option, index) => {
                  const optionId = idStr(getOptionId(option, index));
                  const selected = isCellSelected(rowId, optionId);
                  return (
                    <Button
                      key={optionId}
                      type="button"
                      variant="link"
                      className={[
                        'cc-preview-grid__mobile-option',
                        selected ? 'cc-preview-grid__mobile-option--selected' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      aria-pressed={selected}
                      disabled={!interactive}
                      onClick={() => handleCellClick(rowId, optionId)}
                    >
                      <PreviewChoiceIndicator
                        type={isMulti ? 'checkbox' : 'radio'}
                        selected={selected}
                      />
                      <Typography component="span" variant="body2" className="cc-preview-grid__mobile-option-label">
                        {optionLabel(option)}
                      </Typography>
                    </Button>
                  );
                })}
              </FlexColumn>
            </FlexColumn>
          );
        })}
      </FlexColumn>
    </div>
  );
}
